import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FlatPickrOutputOptions } from 'angularx-flatpickr/lib/flatpickr.directive';
import { Subscription } from 'rxjs';
import { CompanyEmployeePolicy, CompanyPolicyDatetimeType } from '../../../../policy/models/CompanyEmployeePolicy';
import { PolicyService } from '../../../../policy/services/policy.service';
import { FlatpickrHelper } from '../../../../shared/common/FlatpickrHelper';
import { FlatpickrLocaleService } from '../../../../shared/services/flatpickr-locale.service';
import { Employee } from '../../../models/Employee';

export interface AdjustmentHoursResponse {
    date: string;
    hours: CompanyPolicyDatetimeType[];
}

@Component({
    selector: 'change-employee-policy-modal',
    templateUrl: './change-employee-policy-modal.component.html',
    styleUrls: ['./change-employee-policy-modal.component.css']
})

export class ChangeEmployeePolicyModalComponent implements OnInit, OnDestroy {
    public submitted = false;
    public employee: Employee;
    public addPolicyToEmployeeForm: UntypedFormGroup;
    public companyPolicies: CompanyEmployeePolicy[];
    public employeePresetPolicy: CompanyEmployeePolicy;
    public employeeSetPolicy: CompanyEmployeePolicy;
    public selectedStatTypeID: number;
    public selectedStatType: CompanyPolicyDatetimeType;
    public locale$ = this._flatpickrLocale.currentFlatpickrLocale$;
    public effectiveFromMinDate = null;

    private _subscriptions = new Subscription();

    constructor(
        public fpHelper: FlatpickrHelper,
        public activeModal: NgbActiveModal,
        private _policyService: PolicyService,
        private _fb: UntypedFormBuilder,
        private _flatpickrLocale: FlatpickrLocaleService,
    ) { }

    public get employeeDatetimeTypes(): FormArray {
        return this.addPolicyToEmployeeForm?.get('employee_datetime_types') as FormArray;
    }

    public get f(): { [formControlName: string]: AbstractControl; } {
        return this.addPolicyToEmployeeForm.controls;
    }

    public ngOnDestroy(): void {
        this._subscriptions?.unsubscribe();
    }

    public ngOnInit(): void {
        this.addPolicyToEmployeeForm = this._fb.group({
            company_employee_policy_ID: [null, Validators.required],
            effective_from: [null, Validators.required],
            employee_datetime_types: this._fb.array([]),
        });

        this._subscriptions.add(this._policyService.getAllPolicies()
            .subscribe(policies => {
                this.companyPolicies = policies;
                if (this.employee.company_employee_policy_ID) {
                    this.employeePresetPolicy = policies.find(policy => policy.company_employee_policy_ID === this.employee.company_employee_policy_ID);
                    this.addPolicyToEmployeeForm.patchValue({company_employee_policy_ID: this.employee.company_employee_policy_ID});
                    this.addPolicyToEmployeeForm.controls.company_employee_policy_ID.disable();
                    this.addPolicyToEmployeeForm.updateValueAndValidity();

                    if (this.selectedStatTypeID) {
                        this.selectedStatType = this.employeePresetPolicy.employee_datetime_types.find(type => type.employee_datetime_type_ID === this.selectedStatTypeID);
                        this.selectedStatType.hours = null;
                        this.addNewDatetimeType(this.selectedStatType);
                    }
                }
            }));
    }

    public onSubmit(): void {
        this.submitted = true;
        if (this.addPolicyToEmployeeForm.invalid) {
            return;
        }

        const additionalData = {
            ...this.addPolicyToEmployeeForm.value,
        };
        delete additionalData.company_employee_policy_ID;

        if (this.selectedStatTypeID) {
            this._policyService.savePolicyAdjustment(additionalData, this.employee.employee_ID, null)
                .then(() => this.activeModal.close(),
                    () => { });
        } else {
            const policyID = this.addPolicyToEmployeeForm.value.company_employee_policy_ID;
            this._policyService.savePolicytoEmployee(this.employee.employee_ID, policyID, additionalData)
                .then(() => this.activeModal.close(),
                    () => { });
        }
    }

    public onPolicyChange($event: CompanyEmployeePolicy): void {
        this.employeeSetPolicy = $event;
        this.getAdjustmentHours();
    }

    public onDateChange($event: FlatPickrOutputOptions): void {
        if (!this.selectedStatTypeID) {
            this.getAdjustmentHours($event.dateString);
        }
    }

    public getAdjustmentHours(dateValue = null): void {
        this._subscriptions.add(
            this._policyService.getAdjustmentHours(this.employee.employee_ID, this.addPolicyToEmployeeForm.value.company_employee_policy_ID, dateValue || this.addPolicyToEmployeeForm.value.effective_from)
                .subscribe((response: AdjustmentHoursResponse) => {
                    this.employeeDatetimeTypes.clear();

                    response.hours.forEach(type => {
                        this.addNewDatetimeType(type);
                    });

                    this.addPolicyToEmployeeForm.patchValue({
                        effective_from: response.date
                    });
                })
        );
    }

    public datetimeFormGroup(type: CompanyPolicyDatetimeType = null): UntypedFormGroup {
        return this._fb.group({
            employee_datetime_type_ID: [type?.employee_datetime_type_ID || null, Validators.required],
            hours: [type.hours || null, Validators.required],
            name: type.name
        });
    }

    public addNewDatetimeType(type: CompanyPolicyDatetimeType = null): void {
        this.employeeDatetimeTypes.push(this.datetimeFormGroup(type));
    }
}
